import React, { useState, useContext } from "react";
import { DataProvider } from "./contexts/DataContext";
import { UIProvider } from "./contexts/UIContext";

import Main from "./components/Main";
import { ControlProvider, ControlContext } from "./contexts/ControlContext";
import Mobile from "./components/Mobile";
import Header from "./components/Header";
import styled, { ThemeProvider, createGlobalStyle } from "styled-components";
import DisplayLeaderboard from "./components/DisplayLeaderboard";
import LeaderboardHeader from "./components/LeaderboardHeader";
import Splash from "./components/Splash";
import AllPegs from "./components/AllPegs";
import CountEntry from "./components/CountEntry";
import FishCountWeighIn from "./components/FishCountWeighIn";
import useLocalStorage from "./useLocalStorage";

// Thin, theme-aware scrollbars (DESIGN.md §7.6). Transparent track, a quiet
// ~4px rounded thumb in the theme's text-variant tone that deepens on hover.
// References theme tokens so it adapts to light/dark automatically.
const GlobalStyle = createGlobalStyle`
  * {
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.colors.scroll_thumb} transparent;
  }
  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.scroll_thumb};
    border-radius: 999px;
    /* 3px transparent border + padding-box clip renders as a ~4px thumb. */
    border: 3px solid transparent;
    background-clip: padding-box;
  }
  ::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.colors.scroll_thumb_hover};
  }
  ::-webkit-scrollbar-corner {
    background: transparent;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;
const PopupMain = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  justify-content: center;
  align-items: center;
  z-index: 100;
`;
const PopupMessage = styled.div`
  font-size: 2em;
  color: #d61c47;
`;

const Background = styled.div`
  width: 100vw;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

// Atmospheric Precision tokens (see DESIGN.md). Light = Curated Clarity (blue
// accent, soft neutral layers); Dark = Kinetic Console (green accent, deep
// layered surfaces). Components read these semantic roles; the +/- actions keep
// their own status greens/reds so "add" and "remove" read the same in both.
export const light = {
  name: "light-theme",
  colors: {
    background: "#f5f7fa",
    surface_low: "#eef1f4",
    peg_background: "#ffffff",
    surface_high: "#ffffff",
    text: "#2c2f32",
    text_variant: "#5b6b70",
    primary: "#004ce1",
    primary_soft: "#809bff",
    success: "#12b76a",
    danger: "#da1849",
    outline: "rgba(171, 173, 176, 0.28)",
    scroll_thumb: "rgba(91, 107, 112, 0.45)",
    scroll_thumb_hover: "rgba(91, 107, 112, 0.75)",
  },
};

export const dark = {
  name: "dark-theme",
  colors: {
    background: "#060e20",
    surface_low: "#091328",
    peg_background: "#141f38",
    surface_high: "#192540",
    text: "#eaf0ff",
    text_variant: "#a3aac4",
    primary: "#00ec90",
    primary_soft: "#76ffb0",
    success: "#00ec90",
    danger: "#ff4d6d",
    outline: "rgba(64, 72, 93, 0.45)",
    scroll_thumb: "rgba(163, 170, 196, 0.40)",
    scroll_thumb_hover: "rgba(163, 170, 196, 0.72)",
  },
};

var urlParams = new URLSearchParams(window.location.search);

// The fish-count entry screen lives on its own path (served behind basic auth).
var isCountPath = window.location.pathname
  .toLowerCase()
  .replace(/\/+$/, "")
  .endsWith("/count");

// Dedicated page for adjusting the tournament-wide fishCountWeighIn value.
var isFishCountWeighInPath = window.location.pathname
  .toLowerCase()
  .replace(/\/+$/, "")
  .endsWith("/fishcountweighin");

function Popup() {
  const { connected } = useContext(ControlContext);
  if (connected) {
    return <div />;
  }
  return (
    <PopupMain>
      <PopupMessage>Trying to connect to the server...</PopupMessage>
    </PopupMain>
  );
}

function App() {
  const [selectedTheme, setSelectedTheme] = useLocalStorage(
    "fisho_theme",
    "light"
  );
  let website = window.location.hostname;
  let mob = urlParams.get("mobile");
  window.splash = urlParams.get("splash") === "true" || false;
  window.showHeader = urlParams.get("header") !== "false" || false;
  window.device = urlParams.get("device") || "Server";
  window.tournament = urlParams.get("tourn") || 1;
  window.pegs = urlParams
    .get("pegs")
    ?.split(",")
    ?.map((p) => parseInt(p));
  window.leaderboard = urlParams.get("leaderboard") || 0;
  window.page = urlParams.get("page") || 0;
  const [mobile, setMobile] = useState(mob === "true" || false);
  if (window.leaderboard) {
    document.body.style.backgroundColor = "#dddddd";
  }

  const HandleThemeChange = (theme) => {
    setSelectedTheme(theme);
  };

  return (
    <ThemeProvider theme={selectedTheme === "light" ? light : dark}>
      <GlobalStyle />
      <Container>
        <ControlProvider>
          <DataProvider>
            <UIProvider>
              <div></div>
              {window.splash === true && <Splash />}
              {!window.splash && isCountPath && (
                <CountEntry HandleThemeChange={HandleThemeChange} />
              )}
              {!window.splash && isFishCountWeighInPath && <FishCountWeighIn />}
              {!window.splash && !isCountPath && !isFishCountWeighInPath && (
                <>
                  {window.leaderboard == 0 && <Popup />}
                  {!mobile &&
                    window.leaderboard == 0 &&
                    website.indexOf("herokuapp") === -1 &&
                    website.indexOf("hyperstudios") === -1 && (
                      <>
                        <Background>
                          <Header />
                          <Main></Main>
                        </Background>
                      </>
                    )}
                  {mobile &&
                    website.indexOf("herokuapp") === -1 &&
                    website.indexOf("hyperstudios") === -1 &&
                    window.leaderboard == 0 &&
                    parseInt(window.device) > 0 && (
                      <Mobile HandleThemeChange={HandleThemeChange}></Mobile>
                    )}

                  {mobile &&
                    website.indexOf("herokuapp") === -1 &&
                    website.indexOf("hyperstudios") === -1 &&
                    window.leaderboard == 0 &&
                    !parseInt(window.device) && <AllPegs />}
                  {window.leaderboard == 1 && (
                    <>
                      {window.showHeader && <LeaderboardHeader />}
                      <DisplayLeaderboard>Leaderboard</DisplayLeaderboard>
                    </>
                  )}
                </>
              )}
            </UIProvider>
          </DataProvider>
        </ControlProvider>
      </Container>
    </ThemeProvider>
  );
}

export default App;
